import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import PlantillaModal from '../util/PlantillaModal';
import instanciaAxios from '../../miscelaneo/instanciaAxios';
import { esRNCValido } from '../util/ValidacionesCampos';
import InputBusquedaRNC from '../util/Inputs/InputBusquedaRNC';

export default function ModalCrearClienteRapido({ 
  mostrar, 
  onClose, 
  onClienteCreado,
  tiposComprobantesFiscales 
}) {
  const [datosCliente, setDatosCliente] = useState({
    tipo_cliente: 'juridico',
    nombre: '',
    apellido: '',
    razon_social: '',
    rnc: '',
    id_tipo_comprobante_fiscal: '1'
  });

  const limpiarFormulario = () => {
    setDatosCliente({
      tipo_cliente: 'juridico',
      nombre: '',
      apellido: '',
      razon_social: '',
      rnc: '',
      id_tipo_comprobante_fiscal: '1'
    });
  };

  const handleClose = () => {
    limpiarFormulario();
    onClose();
  };

  const crearCliente = async () => {
    // Validaciones
    const errores = [
      // Validación condicional de nombre según tipo
      { 
        cond: datosCliente.tipo_cliente === 'fisico' && !datosCliente.nombre, 
        msg: 'El nombre es obligatorio para clientes físicos' 
      },
      { 
        cond: datosCliente.tipo_cliente === 'juridico' && !datosCliente.nombre && !datosCliente.razon_social, 
        msg: 'Debe proporcionar al menos nombre o razón social para clientes jurídicos' 
      },
      { 
        cond: !datosCliente.rnc, 
        msg: 'El RNC es obligatorio para clientes' 
      },
      { 
        cond: !esRNCValido(datosCliente.rnc), 
        msg: 'El RNC debe tener 9 dígitos (empresas) o 11 dígitos (cédula como RNC)' 
      },
      { cond: !datosCliente.id_tipo_comprobante_fiscal, msg: 'El tipo de comprobante fiscal es obligatorio' }
    ];

    for (let error of errores) {
      if (error.cond) {
        toast.error(error.msg);
        return;
      }
    }
    
    try {
      const nombreFinal = (
        datosCliente.nombre ||
        (datosCliente.tipo_cliente === 'juridico' ? datosCliente.razon_social : '')
      ).trim();
      const razonSocialFinal = (datosCliente.razon_social || nombreFinal).trim();
      const rncLimpio = datosCliente.rnc.replace(/\D/g, '');

      const payload = {
        nombre: nombreFinal,
        apellido: datosCliente.tipo_cliente === 'fisico' ? datosCliente.apellido : null,
        razon_social: datosCliente.tipo_cliente === 'juridico' ? razonSocialFinal : null,
        rnc: rncLimpio || null,
        cedula: null,
        tipos_entidad: [1], // 1 = Cliente
        detallesCliente: {
          tipo_cliente: datosCliente.tipo_cliente,
          id_tipo_comprobante_fiscal: parseInt(datosCliente.id_tipo_comprobante_fiscal)
        }
      };
      
      const response = await instanciaAxios.post('/entidad', payload);
      const clienteCreado = {
        id_entidad: response.data.id_entidad,
        nombre: nombreFinal,
        apellido: datosCliente.apellido,
        razon_social: razonSocialFinal,
        id_tipo_comprobante_fiscal: datosCliente.id_tipo_comprobante_fiscal
      };
      
      toast.success('Cliente creado exitosamente');
      limpiarFormulario();
      onClienteCreado(clienteCreado);
    } catch (error) {
      console.error('Error al crear cliente:', error);
      toast.error(error.response?.data?.mensaje || 'Error al crear cliente');
    }
  };

  const getTiposComprobantePermitidos = () => {
    return tiposComprobantesFiscales.filter(tipo =>
      [1, 2, 8, 9, 10].includes(tipo.id_tipo_comprobante_fiscal)
    );
  };

  useEffect(() => {
    const tiposPermitidos = getTiposComprobantePermitidos();
    const tipoActualPermitido = tiposPermitidos.find(
      t => t.id_tipo_comprobante_fiscal === parseInt(datosCliente.id_tipo_comprobante_fiscal)
    );
    
    if (!tipoActualPermitido && tiposPermitidos.length > 0) {
      // Si el tipo actual no está permitido, cambiar al primero permitido
      setDatosCliente(prev => ({
        ...prev,
        id_tipo_comprobante_fiscal: tiposPermitidos[0].id_tipo_comprobante_fiscal.toString()
      }));
    }
  }, [datosCliente.tipo_cliente, tiposComprobantesFiscales]);

  const handleSeleccionContribuyenteDGII = (contribuyente) => {
    const razonSocial = (contribuyente?.razon_social || '').trim();
    const rnc = contribuyente?.rnc || '';

    setDatosCliente((prev) => ({
      ...prev,
      tipo_cliente: 'juridico',
      rnc,
      razon_social: razonSocial || prev.razon_social,
      nombre: razonSocial || prev.nombre,
      id_tipo_comprobante_fiscal: '1'
    }));
  };

  return (
    <PlantillaModal
      mostrar={mostrar}
      onClose={handleClose}
      titulo="Crear Nuevo Cliente"
    >
      <div className="space-y-4">
        {/* 1. TIPO DE CLIENTE */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tipo de Cliente <span className="text-red-500">*</span>
          </label>
          <select
            value={datosCliente.tipo_cliente}
            onChange={(e) => setDatosCliente({...datosCliente, tipo_cliente: e.target.value})}
            className="w-full p-2 rounded-xl border-2 outline-none transition-all border-red-200 focus:border-red-500 focus:ring-2 focus:ring-red-100"
          >
            <option value="fisico">Físico</option>
            <option value="juridico">Jurídico</option>
          </select>
        </div>
        
        {/* 2. RNC - con búsqueda en padrón DGII */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            RNC <span className="text-red-500">*</span>
          </label>
          <InputBusquedaRNC
            value={datosCliente.rnc}
            onChange={(valor) => setDatosCliente({ ...datosCliente, rnc: valor })}
            onSeleccionar={handleSeleccionContribuyenteDGII}
            placeholder="Buscar por RNC en DGII"
          />
        </div>

        {/* 3. NOMBRE - Obligatorio para físico, opcional para jurídico */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Nombre {datosCliente.tipo_cliente === 'fisico' && <span className="text-red-500">*</span>}
          </label>
          <input
            type="text"
            value={datosCliente.nombre}
            onChange={(e) => setDatosCliente({...datosCliente, nombre: e.target.value})}
            className={`w-full p-2 rounded-xl border-2 outline-none transition-all ${
              datosCliente.tipo_cliente === 'fisico'
                ? 'border-red-200 focus:border-red-500 focus:ring-2 focus:ring-red-100'
                : 'border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100'
            }`}
            placeholder="Nombre del cliente"
          />
        </div>
        
        {/* 4. APELLIDO - Solo para físico */}
        {datosCliente.tipo_cliente === 'fisico' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Apellido
            </label>
            <input
              type="text"
              value={datosCliente.apellido}
              onChange={(e) => setDatosCliente({...datosCliente, apellido: e.target.value})}
              className="w-full p-2 rounded-xl border-2 outline-none transition-all border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              placeholder="Apellido del cliente"
            />
          </div>
        )}
        
        {/* 5. RAZÓN SOCIAL - Solo para jurídico */}
        {datosCliente.tipo_cliente === 'juridico' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Razón Social
            </label>
            <input
              type="text"
              value={datosCliente.razon_social}
              onChange={(e) => setDatosCliente({...datosCliente, razon_social: e.target.value})}
              className="w-full p-2 rounded-xl border-2 outline-none transition-all border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              placeholder="Razón social de la empresa"
            />
            
          </div>
        )}
        
        {/* 6. TIPO DE COMPROBANTE FISCAL */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tipo Comprobante Fiscal <span className="text-red-500">*</span>
          </label>
          <select
            value={datosCliente.id_tipo_comprobante_fiscal}
            onChange={(e) => setDatosCliente({...datosCliente, id_tipo_comprobante_fiscal: e.target.value})}
            className="w-full p-2 rounded-xl border-2 outline-none transition-all border-red-200 focus:border-red-500 focus:ring-2 focus:ring-red-100"
          >
            {getTiposComprobantePermitidos().map((tipo) => (
              <option key={tipo.id_tipo_comprobante_fiscal} value={tipo.id_tipo_comprobante_fiscal}>
                {tipo.descripcion}
              </option>
            ))}
          </select>
        </div>
        
        <button
          onClick={crearCliente}
          className="w-full bg-color-secundario text-white py-3 rounded-xl font-semibold hover:bg-opacity-90 transition-all"
        >
          Crear Cliente
        </button>
      </div>
    </PlantillaModal>
  );
}
